import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { IncidentReport, IncidentState, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const STATION_ROLES = ['STATION_OFFICER'];

const NEXT_STATE: Record<IncidentState, IncidentState | null> = {
  SUBMITTED: 'TRIAGED',
  TRIAGED: 'ASSIGNED',
  ASSIGNED: 'INVESTIGATING',
  INVESTIGATING: 'CLOSED',
  CLOSED: null,
};

/** Police — incident (occurrence book) reports (M05): triage through to closure. */
export function Incidents() {
  const { persona } = useAuth();
  const isStationOfficer = persona ? persona.roles.some((r) => STATION_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    return `/api/v1/police/incidents?${params.toString()}`;
  }, [status]);

  const listQ = useApiQuery<Page<IncidentReport>>(path);

  return (
    <div>
      <PageHeader
        title="Incidents"
        subtitle="Occurrence-book incident reports — citizen-filed or logged at the counter (M05)."
        actions={
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All states</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="TRIAGED">Triaged</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="INVESTIGATING">Investigating</option>
            <option value="CLOSED">Closed</option>
          </select>
        }
      />

      {!isStationOfficer ? <RoleNotice roles={STATION_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No incidents match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">OB ref</th>
                <th className="table-head">Type</th>
                <th className="table-head">Location</th>
                <th className="table-head">Reporter</th>
                <th className="table-head">Station</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((i) => (
                <tr key={i.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(i.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{i.obRef}</td>
                  <td className="table-cell">{i.type.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{i.locationDescription}</td>
                  <td className="table-cell font-mono text-xs">{i.anonymous ? 'Anonymous' : (i.reporterNin ?? '—')}</td>
                  <td className="table-cell text-xs text-slate-500">{i.stationAssigned ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={i.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <IncidentDetailModal
          id={selectedId}
          canAct={isStationOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function IncidentDetailModal({
  id,
  canAct,
  onClose,
  onChanged,
}: {
  id: string;
  canAct: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const q = useApiQuery<IncidentReport>(`/api/v1/police/incidents/${id}`);
  const i = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stationAssigned, setStationAssigned] = useState('');
  const [notes, setNotes] = useState('');

  const next = i ? NEXT_STATE[i.state] : null;

  const triage = async () => {
    if (!next) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/police/incidents/${id}/triage`, {
        state: next,
        ...(stationAssigned.trim() ? { stationAssigned: stationAssigned.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={i ? `${i.obRef} — ${i.type.replaceAll('_', ' ')}` : 'Incident report'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {i ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="State" value={<StatusBadge status={i.state} />} />
            <Detail label="Type" value={i.type.replaceAll('_', ' ')} />
            <Detail label="Location" value={i.locationDescription} />
            <Detail label="Reporter" value={i.anonymous ? 'Anonymous' : (i.reporterNin ?? '—')} mono />
            <Detail label="Station assigned" value={i.stationAssigned ?? '—'} />
            <Detail label="Media attachments" value={String(i.media?.length ?? 0)} />
          </div>
          <Detail label="Narrative" value={i.narrative} />
          {i.closedNotes ? <Detail label="Closing notes" value={i.closedNotes} /> : null}

          {canAct && next ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700">Move to {next.replaceAll('_', ' ')}</div>
              {next !== 'CLOSED' ? (
                <Field label="Station assigned (optional)">
                  <input className="field-input" value={stationAssigned} onChange={(e) => setStationAssigned(e.target.value)} placeholder="e.g. Central Police Station" />
                </Field>
              ) : (
                <Field label="Closing notes (optional)">
                  <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </Field>
              )}
              <button className="btn-primary" disabled={busy} onClick={triage}>
                {busy ? 'Updating…' : `Confirm move to ${next.replaceAll('_', ' ')}`}
              </button>
            </div>
          ) : !canAct && next ? (
            <RoleNotice roles={STATION_ROLES} />
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Filed {formatDateTime(i.createdAt)} · last updated {formatDateTime(i.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
